import { QuantityBreaksUpdatedEvent } from '@theme/events';

class BundleCurePicker extends HTMLElement {
  #packs = [];
  #flavorOptions = [];

  connectedCallback() {
    this.#organizeBlocks();
    this.#flavorOptions = this.#getFlavorOptions();
    this.#renderPackSelectors();
    this.#bindPacks();
    this.#initializeDefaults();
    this.#syncState();
    document.addEventListener('click', this.#handleDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  #handleDocumentClick = (event) => {
    if (!event.target.closest('.bundle-cure-picker-flavor-select')) {
      this.#closeAllFlavorSelects();
    }
  };

  #organizeBlocks() {
    const blocksRoot = this.querySelector('[data-ref="bundle-cure-picker-blocks"]');
    const packsWrapper = this.querySelector('[data-ref="bundle-cure-picker-packs"]');
    const flavorsWrapper = this.querySelector('[data-ref="bundle-cure-picker-flavors"]');

    if (!blocksRoot) return;

    for (const pack of Array.from(blocksRoot.querySelectorAll('[data-ref="bundle-cure-picker-pack"]'))) {
      packsWrapper?.appendChild(pack);
    }

    for (const flavor of Array.from(blocksRoot.querySelectorAll('[data-ref="bundle-cure-picker-flavor"]'))) {
      flavorsWrapper?.appendChild(flavor);
    }

    blocksRoot.remove();
  }

  #getPacks() {
    return Array.from(this.querySelectorAll('[data-ref="bundle-cure-picker-pack"]'));
  }

  #getFlavorOptions() {
    return Array.from(this.querySelectorAll('[data-ref="bundle-cure-picker-flavor"]'))
      .filter((node) => node.dataset.variantId)
      .map((node) => ({
        variantId: node.dataset.variantId,
        title: node.dataset.productTitle || 'Option',
        variantImage: node.dataset.variantImage || '',
        variantImageAlt: node.dataset.variantImageAlt || '',
        isFreeProduct: node.dataset.isFreeProduct === 'true',
      }));
  }

  #getFlavorOption(variantId) {
    return this.#flavorOptions.find((option) => option.variantId === variantId) || null;
  }

  #createFlavorImageMarkup(imageUrl, imageAlt, size = 28) {
    if (!imageUrl) return '';

    return `<img src="${imageUrl}" alt="${imageAlt}" width="${size}" height="${size}" loading="lazy">`;
  }

  #createFlavorSelectTriggerIcon() {
    const iconStyle = this.dataset.iconStyle?.trim() || 'outlined';
    const icon = document.createElement('span');
    icon.className = `material-symbols-${iconStyle} notranslate icon icon--keyboard_arrow_down`;
    icon.setAttribute('translate', 'no');
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = 'keyboard_arrow_down';
    return icon;
  }

  #updateFlavorSelectUI(wrapper) {
    const value = wrapper.dataset.value;
    const option = this.#getFlavorOption(value) || this.#flavorOptions[0];
    const trigger = wrapper.querySelector('[data-ref="bundle-cure-picker-flavor-select-trigger"]');

    if (!trigger || !option) return;

    trigger.replaceChildren();

    if (option.variantImage) {
      const media = document.createElement('span');
      media.className = 'bundle-cure-picker-flavor-select__trigger-media';
      media.innerHTML = this.#createFlavorImageMarkup(option.variantImage, option.variantImageAlt);
      trigger.appendChild(media);
    }

    const label = document.createElement('span');
    label.className = 'bundle-cure-picker-flavor-select__trigger-label';
    label.textContent = option.title;
    trigger.appendChild(label);

    const icon = document.createElement('span');
    icon.className = 'bundle-cure-picker-flavor-select__trigger-icon';
    icon.appendChild(this.#createFlavorSelectTriggerIcon());
    trigger.appendChild(icon);

    for (const item of wrapper.querySelectorAll('[data-ref="bundle-cure-picker-flavor-select-option"]')) {
      const isSelected = item.dataset.value === value;
      item.classList.toggle('is-selected', isSelected);
      item.setAttribute('aria-selected', String(isSelected));
    }
  }

  #setFlavorSelectValue(wrapper, variantId, { close = true } = {}) {
    if (!variantId) return;

    wrapper.dataset.value = variantId;
    this.#updateFlavorSelectUI(wrapper);

    if (close) {
      this.#closeFlavorSelect(wrapper);
    }
  }

  #closeFlavorSelect(wrapper) {
    wrapper.classList.remove('is-open');

    const list = wrapper.querySelector('[data-ref="bundle-cure-picker-flavor-select-list"]');
    const trigger = wrapper.querySelector('[data-ref="bundle-cure-picker-flavor-select-trigger"]');

    if (list) list.hidden = true;
    trigger?.setAttribute('aria-expanded', 'false');
  }

  #closeAllFlavorSelects() {
    for (const wrapper of this.querySelectorAll('.bundle-cure-picker-flavor-select.is-open')) {
      this.#closeFlavorSelect(wrapper);
    }
  }

  #toggleFlavorSelect(wrapper) {
    const isOpen = wrapper.classList.contains('is-open');
    this.#closeAllFlavorSelects();

    if (isOpen) return;

    const list = wrapper.querySelector('[data-ref="bundle-cure-picker-flavor-select-list"]');
    const trigger = wrapper.querySelector('[data-ref="bundle-cure-picker-flavor-select-trigger"]');

    wrapper.classList.add('is-open');
    if (list) list.hidden = false;
    trigger?.setAttribute('aria-expanded', 'true');
  }

  #createFlavorSelect(pack, options, index) {
    const wrapper = document.createElement('div');
    wrapper.className = 'bundle-cure-picker-flavor-select';
    wrapper.dataset.ref = 'bundle-cure-picker-flavor-select';
    wrapper.dataset.value = options[0]?.variantId || '';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'bundle-cure-picker-flavor-select__trigger';
    trigger.dataset.ref = 'bundle-cure-picker-flavor-select-trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-label', `Saveur ${index + 1}`);

    const list = document.createElement('ul');
    list.className = 'bundle-cure-picker-flavor-select__list';
    list.dataset.ref = 'bundle-cure-picker-flavor-select-list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    for (const option of options) {
      const item = document.createElement('li');
      item.className = 'bundle-cure-picker-flavor-select__option';
      item.dataset.ref = 'bundle-cure-picker-flavor-select-option';
      item.dataset.value = option.variantId;
      item.dataset.isFreeProduct = String(option.isFreeProduct);
      item.setAttribute('role', 'option');

      const media = document.createElement('span');
      media.className = 'bundle-cure-picker-flavor-select__option-media';

      if (option.variantImage) {
        media.innerHTML = this.#createFlavorImageMarkup(option.variantImage, option.variantImageAlt, 32);
      }

      const label = document.createElement('span');
      label.className = 'bundle-cure-picker-flavor-select__option-label';
      label.textContent = option.title;

      item.appendChild(media);
      item.appendChild(label);
      list.appendChild(item);
    }

    wrapper.appendChild(trigger);
    wrapper.appendChild(list);
    this.#updateFlavorSelectUI(wrapper);

    const stopPackSelection = (event) => {
      event.stopPropagation();
    };

    trigger.addEventListener('click', (event) => {
      stopPackSelection(event);
      event.preventDefault();
      this.#toggleFlavorSelect(wrapper);
    });

    list.addEventListener('click', (event) => {
      stopPackSelection(event);

      const option = event.target.closest('[data-ref="bundle-cure-picker-flavor-select-option"]');
      if (!option) return;

      this.#setFlavorSelectValue(wrapper, option.dataset.value);

      if (!pack.classList.contains('is-selected')) {
        this.#selectPack(pack);
        return;
      }

      this.#syncState();
      document.dispatchEvent(new QuantityBreaksUpdatedEvent());
    });

    return wrapper;
  }

  #getPackDefaultVariantIds(pack) {
    const count = parseInt(pack.dataset.targetQuantity, 10) || 0;
    const configured = (pack.dataset.defaultFlavorVariantIds || '')
      .split(',')
      .map((variantId) => variantId.trim())
      .filter(Boolean);
    const optionIds = this.#flavorOptions.map((option) => option.variantId);

    return Array.from({ length: count }, (_, index) => configured[index] || optionIds[index] || optionIds[0] || '').filter(
      Boolean,
    );
  }

  #applyPackDefaultFlavors(pack) {
    if (!pack) return;

    const defaults = this.#getPackDefaultVariantIds(pack);
    const selects = pack.querySelectorAll('[data-ref="bundle-cure-picker-flavor-select"]');

    selects.forEach((select, index) => {
      const variantId = defaults[index] || defaults[0];
      if (variantId) {
        this.#setFlavorSelectValue(select, variantId, { close: false });
      }
    });
  }

  #renderPackSelectors() {
    const options = this.#flavorOptions;

    for (const pack of this.#getPacks()) {
      const container = pack.querySelector('[data-ref="bundle-cure-picker-pack-selectors"]');
      if (!container) continue;

      const count = parseInt(pack.dataset.targetQuantity, 10) || 0;
      container.replaceChildren();

      if (count <= 0 || options.length === 0) {
        continue;
      }

      for (let index = 0; index < count; index += 1) {
        const row = document.createElement('div');
        row.className = 'bundle-cure-picker-pack__selector-row';

        const indexLabel = document.createElement('span');
        indexLabel.className = 'bundle-cure-picker-pack__selector-index';
        indexLabel.textContent = count > 1 ? `#${index + 1}` : '#1';

        const flavorSelect = this.#createFlavorSelect(pack, options, index + 1);

        row.appendChild(indexLabel);
        row.appendChild(flavorSelect);
        container.appendChild(row);
      }

      this.#applyPackDefaultFlavors(pack);
    }
  }

  #bindPacks() {
    for (const pack of this.#getPacks()) {
      pack.addEventListener('click', (event) => {
        if (event.target.closest('[data-ref="bundle-cure-picker-pack-selectors"]')) return;
        this.#selectPack(pack);
      });

      pack.addEventListener('keydown', (event) => {
        if (event.target.closest('[data-ref="bundle-cure-picker-pack-selectors"]')) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        this.#selectPack(pack);
      });
    }
  }

  #initializeDefaults() {
    const preselectedPack = this.#getPacks().find((pack) => pack.dataset.preselected === 'true') || this.#getPacks()[0];

    if (preselectedPack) {
      this.#selectPack(preselectedPack);
    }
  }

  #selectPack(pack) {
    for (const currentPack of this.#getPacks()) {
      const isSelected = currentPack === pack;
      currentPack.classList.toggle('is-selected', isSelected);
      currentPack.setAttribute('aria-pressed', String(isSelected));
    }

    this.#applyPackDefaultFlavors(pack);
    this.#syncState();
    document.dispatchEvent(new QuantityBreaksUpdatedEvent());
  }

  #getSelectedPack() {
    return this.#getPacks().find((pack) => pack.classList.contains('is-selected')) || null;
  }

  #getPackFlavorSelections(pack) {
    if (!pack) return [];

    return Array.from(pack.querySelectorAll('[data-ref="bundle-cure-picker-flavor-select"]'))
      .map((select) => {
        const variantId = select.dataset.value;
        if (!variantId) return null;

        const option = this.#getFlavorOption(variantId);

        return {
          variantId,
          title: option?.title || '',
          isFreeProduct: option?.isFreeProduct ?? true,
        };
      })
      .filter(Boolean);
  }

  #getSelectedFlavorCount() {
    return this.#getPackFlavorSelections(this.#getSelectedPack()).length;
  }

  #getRequiredFlavorCount() {
    const selectedPack = this.#getSelectedPack();
    return parseInt(selectedPack?.dataset.targetQuantity, 10) || 0;
  }

  #syncState() {
    const helper = this.querySelector('[data-ref="bundle-cure-picker-helper"]');
    if (!helper) return;

    const required = this.#getRequiredFlavorCount();
    const selected = this.#getSelectedFlavorCount();
    const missing = Math.max(0, required - selected);

    if (missing <= 0 || required <= 0) {
      helper.hidden = true;
      return;
    }

    const helperLabelTemplate = this.dataset.labelMoreFlavors || 'Ajoutez [count] saveur(s) pour continuer';
    helper.hidden = false;
    helper.textContent = helperLabelTemplate.replace('[count]', String(missing));
  }

  #getSelectedPackVariant() {
    const selectedPack = this.#getSelectedPack();
    const variantId = selectedPack?.dataset.productVariantId;

    if (!variantId) return null;

    return {
      id: variantId,
      quantity: 1,
      price: parseInt(selectedPack.dataset.productVariantPrice, 10) || 0,
      compareAtPrice:
        parseInt(selectedPack.dataset.productVariantCompareAtPrice, 10) ||
        parseInt(selectedPack.dataset.productVariantPrice, 10) ||
        0,
    };
  }

  #getSelectedPackAddonItems() {
    const selectedPack = this.#getSelectedPack();
    if (!selectedPack) return [];

    const cartProducts = selectedPack.querySelectorAll('[data-ref="bundle-builder-pack-cart-product"]');

    return Array.from(cartProducts)
      .map((entry) => ({
        id: entry.dataset.variantId,
        quantity: parseInt(entry.dataset.quantity, 10) || 0,
        properties: { _cure: 'true', _cure_scope: 'pack_addon' },
        price: parseInt(entry.dataset.price, 10) || 0,
        compareAtPrice: parseInt(entry.dataset.compareAtPrice, 10) || parseInt(entry.dataset.price, 10) || 0,
      }))
      .filter((item) => item.id && item.quantity > 0);
  }

  #getSelectedFlavorItems() {
    const selections = this.#getPackFlavorSelections(this.#getSelectedPack());
    const grouped = new Map();

    for (const selection of selections) {
      grouped.set(selection.variantId, (grouped.get(selection.variantId) || 0) + 1);
    }

    return Array.from(grouped.entries()).map(([variantId, quantity]) => {
      const matchingOption = this.#flavorOptions.find((option) => option.variantId === variantId);
      const isFreeProduct = matchingOption?.isFreeProduct ?? true;

      return {
        id: variantId,
        quantity,
        ...(isFreeProduct ? { properties: { _cure: 'true', _cure_scope: 'flacon' } } : {}),
      };
    });
  }

  isSelectionComplete() {
    const selectedPack = this.#getSelectedPack();
    if (!selectedPack) return false;

    const required = this.#getRequiredFlavorCount();
    if (required <= 0) return false;

    return this.#getSelectedFlavorCount() >= required;
  }

  getSelectionProgress() {
    const required = this.#getRequiredFlavorCount();
    if (required <= 0) return null;

    const selected = this.#getSelectedFlavorCount();
    if (selected >= required) return null;

    return { selected, required };
  }

  getSelectedAddonItems() {
    return [...this.#getSelectedPackAddonItems(), ...this.#getSelectedFlavorItems()];
  }

  getSelectedProperties() {
    const properties = {};
    const selectedPack = this.#getSelectedPack();

    if (!selectedPack) return properties;

    if (selectedPack.dataset.packTitle) {
      properties._bundle_pack_duration = selectedPack.dataset.packTitle;
    }

    const fromMetafieldOrData = parseInt(selectedPack?.dataset.cureFlaconBudget, 10);
    const targetQty = parseInt(selectedPack?.dataset.targetQuantity, 10) || 0;
    const cureBudget = Number.isFinite(fromMetafieldOrData) && fromMetafieldOrData > 0 ? fromMetafieldOrData : targetQty;

    if (cureBudget > 0) {
      properties._cure_flacon_budget = String(cureBudget);
    }

    properties._bundle_builder = 'true';

    const packCartText = selectedPack.dataset.cartText?.trim();
    if (packCartText) {
      properties._bundle_line_1 = packCartText;
    }

    return properties;
  }

  getPrimaryCartItem(form) {
    const selectedPackVariant = this.#getSelectedPackVariant();
    const fallbackVariantId = form?.querySelector('input[name="id"]')?.value;
    const quantity = parseInt(form?.querySelector('input[name="quantity"]')?.value, 10) || 1;

    if (selectedPackVariant?.id) {
      return {
        id: selectedPackVariant.id,
        quantity,
      };
    }

    if (!fallbackVariantId) return null;

    return {
      id: fallbackVariantId,
      quantity,
    };
  }

  getSelectedTotalPrice() {
    const selectedPackVariant = this.#getSelectedPackVariant();
    return selectedPackVariant ? selectedPackVariant.price * selectedPackVariant.quantity : 0;
  }

  getSelectedTotalCompareAtPrice() {
    const selectedPackVariant = this.#getSelectedPackVariant();
    if (!selectedPackVariant) return 0;

    return selectedPackVariant.compareAtPrice * selectedPackVariant.quantity;
  }

  shouldReplaceMainProduct() {
    return true;
  }
}

if (!customElements.get('bundle-cure-picker')) {
  customElements.define('bundle-cure-picker', BundleCurePicker);
}
